
import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Bell, TrendingUp, Zap } from "lucide-react";
import toast from "react-hot-toast";
import { inputPlaceholders } from "@/lib/placeholders";
import { newsletterApi } from "@/lib/api";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await newsletterApi.subscribe(email);
      toast.success(response.message || "Thank you for subscribing! You'll receive our latest updates.");
      setEmail("");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || "Failed to subscribe. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-10 left-10 w-64 h-64 bg-aarsa-gold-200/20 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-10 right-10 w-96 h-96 bg-aarsa-sage-300/20 rounded-full blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 mb-6 px-4 py-2 rounded-full bg-aarsa-gold-500/20 backdrop-blur-sm border border-aarsa-gold-400/30"
          >
            <Bell className="w-5 h-5 text-aarsa-gold-300" />
            <span className="text-aarsa-gold-200 font-semibold text-sm">Stay Updated</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl font-bold font-playfair text-white mb-4">
            Stay in the Loop
          </h2>
          <p className="text-lg text-gray-200 mb-3">
            Get exclusive access to our latest collections, trends, and insider updates
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
            <div className="flex items-center space-x-2 text-gray-300">
              <TrendingUp className="w-4 h-4 text-aarsa-gold-400" />
              <span>New arrivals before anyone else</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-300">
              <Zap className="w-4 h-4 text-aarsa-sage-400" />
              <span>Exclusive offers & early access</span>
            </div>
          </div>
          
          <p className="text-sm text-gray-400 mb-8 max-w-lg mx-auto">
            Join our newsletter community and never miss out on what's new. 
            We respect your inbox - expect thoughtful updates, not spam.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <div className="flex-1 relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={inputPlaceholders.email}
                className="w-full pl-12 pr-4 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-aarsa-gold-500/50 focus:border-aarsa-gold-400/50"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative px-8 py-4 rounded-full bg-gradient-to-r from-aarsa-sage-600 to-aarsa-sage-700 text-white font-semibold shadow-lg hover:shadow-xl hover:from-aarsa-sage-700 hover:to-aarsa-sage-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer overflow-hidden"
            >
              {/* Shine Effect */}
              {!isSubmitting && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              )}
              <span className="relative z-10">{isSubmitting ? "Subscribing..." : "Subscribe"}</span>
            </button>
          </form>

          <p className="text-xs text-gray-500 mt-3">
            You are signing up to receive communication via email and can unsubscribe at any time.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
